// IconButton — the ONE icon-only button (added 2026-09-18, audit C-15 / L16,
// batch 14). Before it, nine consumer sites hand-rolled `Button size="s"
// class="btn-circle"` and RowActions carried its own <button> recipe, so the
// icon-only shape was decided in ten places: three glyph sizes (11–14px), two
// hit boxes (24 and 28px, both under the §8 floor), an accent hover on the row
// glyphs and `disabled:opacity-30`. Now RowActions renders one of these per
// action, CopyButton is one with a clipboard behind it, and a module mounts it
// wherever a glyph is the whole control.
//
// THE KIT'S OWN (2026-09-19, audit B14 follow-up): the eight icon-only
// buttons the kit itself rendered — Modal and SidePanel close, the panel's
// expand toggle, Toast and InlineAlert dismiss, SearchableSelect clear, the
// PasswordInput reveal and ImageUpload remove — were hand-rolled `btn
// btn-ghost btn-sm btn-circle`: a pill/circle with daisyUI's ghost hover,
// which spectrum.css §2 exempts from the ActionButton square on purpose (the
// exemption exists for the camera chrome in lib's scanner). So the product
// carried two shapes for one duty. All eight now render this component;
// scripts/check-structure.mjs fails a new `btn-circle` outside this
// directory, the daisyUI review pages and the scanner's recorded sites.
// The rung per duty: `m` for a header or alert control (Modal, SidePanel,
// InlineAlert — Spectrum's 18px workflow icon beside a title), `s` inside a
// field, chip or toast (the 16px row glyph). A close/dismiss/clear X carries
// no tooltip — Adobe's CloseButton and ClearButton have none and the glyph is
// universal; a STATE toggle (expand, reveal) keeps it, because the label
// names the state the press will produce.
//
// SPECTRUM DUTY: an ActionButton (spectrum.adobe.com/page/action-button) —
// `quiet` (the default) has no ground at rest and a gray hover; `outline` is
// the non-quiet form with the gray-200 stroke, for a control floating over a
// thumbnail or photo where a quiet glyph would vanish. It is a rounded SQUARE
// on the ActionButton corner ramp, never a pill (object-styles: pills are the
// CTA family) — the circle the row glyphs used to wear was the libms shape.
//
// GEOMETRY:
//   box     `size-(--ds-target)` — the component-height-100 hit floor (32px
//           desktop, 40 at touch; spectrum.css §8). BOTH sizes share it: the
//           §8 law fixes the target, so `size` moves the GLYPH, not the box.
//           The bracketed fallback keeps a skin-less mount at the old 28px
//           instead of collapsing to auto.
//   radius  `rounded-500` = 8px = ActionButton M — Adobe keys the corner to
//           the button's HEIGHT, and the box is the 32px M rung in both sizes
//           (the four --ds-action-radius steps in spectrum.css §2 are the same
//           ramp, driven off .btn-* heights this component does not wear).
//   glyph   `s` 16px / stroke 2.25 (the row-action tuning — a 1.50px rendered
//           stroke against the S label stem), `m` 18px / 2.33 (Spectrum's
//           desktop workflow-icon size). Delivered by spectrum.css §2 through
//           `data-icon-button="s|m"` from the --ds-btn-icon-* / --ds-btn-
//           stroke-* ramps; the numeric `size` on the icon is only the no-skin
//           fallback.
//
// COLOUR (DEV-GUIDE-002 §12.6, the control-state ramp): rest ink
// text-base-muted; hover/key-focus = bg-(--ui-state-hover) with text-base-
// content (NEVER the accent fill — an accent hover reads as a CTA, which a row
// glyph is not); down = --ui-state-down; `danger` hovers to the error two-tone
// pair (bg-error-tint + text-error); disabled = text-base-disabled +
// cursor-not-allowed, no opacity (§12.6: transparency is the scrim, the shadow,
// the state overlays and skeleton shimmer, nothing else).
//
// TOOLTIP: daisyUI's `tooltip` + `data-tip` on a WRAPPER span, not on the
// button. daisyUI 5.7.17 shows the bubble on `.tooltip:hover` and
// `.tooltip:has(:focus-visible)` — a focused DESCENDANT — so a `.tooltip` class
// on the button itself never showed on keyboard focus (the old RowActions
// recipe). The wrapper form is daisyUI's own, and it makes the tip reach Tab.
// A disabled button gets no `data-tip` (daisyUI's `[data-tip]:not([data-tip=""])`
// guard), so the verb stops explaining itself once the row's state forbids it —
// put the reason in the row. The staging @erp/ui/tooltip (a cursor-tracking
// label with zero importers) retired with this component (decision D10).
//
// THE SQUARE ONES (run 5, 2026-09-19): the btn-circle gate left seven
// `btn btn-ghost btn-sm btn-square` glyphs in the kit — Toolbar's compact
// Export and its Refresh, ListView's Filters opener, RichEditor's ten
// formatting toggles, RepeaterField's move-up / move-down / remove and
// ActionBar's Clear selection. Same duty, a third recipe (daisyUI's square
// with its own hover and a 24px `btn-xs` box under the §8 floor). All render
// this component now, which grew exactly what those sites needed and nothing
// more: `expanded` + `haspopup` (a glyph that opens a dialog or menu),
// `onMouseDown` (an editor toolbar must not steal the selection), a VISUAL for
// `pressed` (Spectrum's selected ActionButton: the neutral fill, gray-800 on
// gray-50 — `.is-selected` in components/actionbutton/index.css aliases
// --spectrum-actionbutton-background-color-selected to the neutral
// background, which base.css already mints as daisyUI `neutral` /
// `neutral-content`), and an `on-accent` variant for a glyph on the accent
// ground (Spectrum's staticColor ActionButton, in the theme's own
// primary-content ink rather than a literal white — the darkest ramp puts
// black on blue-900).
//
// The <button> is BARE — no `.btn` — so the global focus ring in tokens.css
// reaches it and none of the .btn cascade (pill, label ramp, pending overlay)
// has to be fought off. `type="button"` always: an icon inside a form is never
// its submit.
import { Show, type Component } from 'solid-js';
import { Dynamic } from 'solid-js/web';

/** `s` = the 16px row/cell glyph (default); `m` = Spectrum's 18px desktop workflow icon, for toolbars and headers. The BOX is the 32px hit floor in both. */
export type IconButtonSize = 's' | 'm';
/** `quiet` (default): no ground at rest, gray hover — a glyph in a row, a chip, a field. `outline`: base-100 ground + base-300 stroke, for a control over a thumbnail or photo. `on-accent`: primary-content ink with the same state overlays, for a glyph on a SOLID 900 ground — the accent ground (an emphasized ActionBar) or a solid toast; base.css mints the same white (light) / black (darkest) content pair for every role fill, so one variant serves them all. */
export type IconButtonVariant = 'quiet' | 'outline' | 'on-accent';
/** What a popup-opening glyph opens — the `aria-haspopup` token. */
export type IconButtonPopup = 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid' | true;
/** Where the tooltip bubble opens. `top` is daisyUI's default; a glyph on the last row of a table wants `left`. */
export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

/** Tailwind cannot see a class built at runtime, so the four are literal. */
const PLACEMENT: Record<TooltipPlacement, string> = {
  top: 'tooltip-top',
  bottom: 'tooltip-bottom',
  left: 'tooltip-left',
  right: 'tooltip-right',
};

/** The no-skin fallback glyph sizes; spectrum.css §2 overrides both through `data-icon-button`. */
const GLYPH: Record<IconButtonSize, number> = { s: 16, m: 18 };

export interface IconButtonProps {
  /** A lucide-solid icon COMPONENT — the whole visible control, so it is REQUIRED. */
  icon: Component<{ size?: number; class?: string }>;
  /** The verb ("Copy ISBN", "Remove line 3"): the `aria-label` (the button has no text, so this is its only name) and the tooltip. */
  label: string;
  /** The action. Receives the MouseEvent so a glyph inside a clickable row can `stopPropagation()`. */
  onClick?: (e: MouseEvent) => void;
  /** Destructive: hovers to the error two-tone pair (bg-error-tint + text-error). Open a ConfirmDialog from it rather than deleting on the click. */
  danger?: boolean;
  /**
   * Paint the glyph on its role's TINTED CIRCLE instead of the quiet square —
   * an opaque 100 ground with the role's own ink, the two-tone pair. For a
   * glyph that must read as its role at a glance (a row's edit, print and
   * delete on a card). Omit it for the resting ActionButton shape, which is
   * what a dense table wants.
   */
  tone?: IconButtonTone;
  /** Rendered but inert: text-base-disabled, cursor-not-allowed, no tooltip (the reason belongs in the row, not in a bubble the pointer cannot reach). */
  disabled?: boolean;
  /** `s` (default) 16px glyph; `m` 18px. The box is the 32px hit floor either way — see the header. */
  size?: IconButtonSize;
  /** `quiet` (default) or `outline`. */
  variant?: IconButtonVariant;
  /** Show the label as a daisyUI tooltip (default true). `false` where the label is already printed beside the glyph. */
  tooltip?: boolean;
  /** Tooltip side (default `top`). */
  placement?: TooltipPlacement;
  /** Automation id on the <button>. RowActions mints `${idPrefix}-${slug(label)}`; a module site names its own (`btn-<screen>-<verb>-<key>`). */
  id?: string;
  /** Extra classes on the OUTERMOST element (the tooltip wrapper, or the button when `tooltip` is false) — layout only: `shrink-0`, `absolute right-2 top-2`, never colour or size. */
  class?: string;
  /** A pressed/toggled state for a glyph that is a switch (Bold on, flashlight on) — sets `aria-pressed` and paints Spectrum's selected ActionButton (the neutral fill) while true. Leave undefined for a one-shot verb. */
  pressed?: boolean;
  /** The id of the element the glyph acts on, for `aria-controls` — a reveal button names its input, an expand toggle its panel. */
  controls?: string;
  /** A glyph that opens a popup (a filter dialog, a menu): `aria-expanded`, read live. Pair with `haspopup`. */
  expanded?: boolean;
  /** What the glyph opens — `aria-haspopup` (`dialog`, `menu`, …). */
  haspopup?: IconButtonPopup;
  /** Before the click, on the press. An editor's toolbar calls `e.preventDefault()` here so the button does not take focus and collapse the selection it is about to format. */
  onMouseDown?: (e: MouseEvent) => void;
}

/** Spectrum's quiet ActionButton — the ramp's four state overlays, `enabled:` so a disabled glyph stays flat. */
const QUIET =
  'enabled:hover:bg-(--ui-state-hover) enabled:hover:text-base-content enabled:focus-visible:bg-(--ui-state-hover) enabled:focus-visible:text-base-content enabled:active:bg-(--ui-state-down)';
/** Danger swings hover and key-focus onto the error two-tone pair. */
const QUIET_DANGER =
  'enabled:hover:bg-error-tint enabled:hover:text-error enabled:focus-visible:bg-error-tint enabled:focus-visible:text-error enabled:active:bg-error-tint';
/** The non-quiet form: an OPAQUE layer stop under the glyph (base-100 → base-200 hover → base-300 down) — it floats over images, so the translucent ramp would show the photo through. */
const OUTLINE =
  'border border-base-300 bg-base-100 enabled:hover:bg-base-200 enabled:hover:text-base-content enabled:focus-visible:bg-base-200 enabled:focus-visible:text-base-content enabled:active:bg-base-300';
const OUTLINE_DANGER =
  'border border-base-300 bg-base-100 enabled:hover:bg-error-tint enabled:hover:text-error enabled:hover:border-error enabled:focus-visible:bg-error-tint enabled:focus-visible:text-error enabled:focus-visible:border-error enabled:active:bg-error-tint';
/** On the accent ground: the theme's primary-content ink at rest AND under the overlays (the quiet ramp's `text-base-content` hover would vanish on blue-900). */
const ON_ACCENT =
  'text-primary-content enabled:hover:bg-(--ui-state-hover) enabled:hover:text-primary-content enabled:focus-visible:bg-(--ui-state-hover) enabled:focus-visible:text-primary-content enabled:active:bg-(--ui-state-down) disabled:text-primary-content';
/**
 * Spectrum's selected ActionButton (`.is-selected`): the neutral fill —
 * gray-800 ground, gray-50 content, the next stop (gray-900) on hover and down
 * (base.css: --color-neutral / --color-neutral-content / --ds-neutral-*).
 * Stacked with `enabled:hover:` so it outranks the quiet ramp's hover on the
 * same element; a pressed toggle never falls back to the gray overlay.
 */
const SELECTED =
  'aria-pressed:bg-neutral aria-pressed:text-neutral-content aria-pressed:enabled:hover:bg-(--ds-neutral-hover) aria-pressed:enabled:hover:text-neutral-content aria-pressed:enabled:focus-visible:bg-(--ds-neutral-hover) aria-pressed:enabled:focus-visible:text-neutral-content aria-pressed:enabled:active:bg-(--ds-neutral-down)';

/** The class recipe, exported so a test (or RowActions' own) can pin the geometry without rendering. */

/* THE TINTED CIRCLE (2026-10-01, the owner: "circle background with their
   respective spectrum colour"). Each is a TWO-TONE PAIR — the role's 100 tint
   as an opaque ground, the role's own ink on it — which is this repo's law for
   a soft status surface, never an alpha and never a solid fill. A solid fill
   here would be a CTA's treatment repeated four times a row, which is exactly
   what audit C-15 took OUT of RowActions; the tint carries the colour without
   the shout.

   Complete static strings per tone, never assembled: Tailwind cannot see a
   class built at runtime. */
export type IconButtonTone = 'neutral' | 'accent' | 'negative';

/* ONLY THREE, and the lint is why — it caught a wider set and was right.
   DEV-GUIDE-002 §12.4 / decision D4: coloured ink is ACCENT (links, the one
   action a view is for) and NEGATIVE (owed amounts, validation) only; success,
   warning and info are banned as ink and belong to a StatusBadge. A row's
   verbs are edit (accent), the ordinary ones (neutral) and the destructive one
   (negative) — which is the whole vocabulary this needs anyway.

   The ground is an opaque 100 tint and the ink the role's own: the two-tone
   pair, never an alpha, never a solid fill. A solid fill here would be a CTA's
   treatment repeated four times a row, which is exactly what audit C-15 took
   out of RowActions; the tint carries the colour without the shout.

   Complete static strings per tone, never assembled: Tailwind cannot see a
   class built at runtime. */
const TONE: Record<IconButtonTone, string> = {
  neutral: 'bg-base-200 text-base-muted hover:bg-base-300',
  accent: 'bg-primary-tint text-primary hover:bg-primary-tint',
  negative: 'bg-error-tint text-error hover:bg-error-tint',
};

export const iconButtonClasses = (o: { variant?: IconButtonVariant; danger?: boolean; tone?: IconButtonTone }): string => {
  // A tone replaces the quiet ramp entirely: ground, ink and a full radius.
  if (o.tone) {
    return `inline-flex size-[var(--ds-target,1.75rem)] shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:bg-base-200 disabled:text-base-disabled ${TONE[o.tone]}`;
  }
  const state =
    o.variant === 'on-accent'
      ? ON_ACCENT
      : o.variant === 'outline'
        ? o.danger
          ? OUTLINE_DANGER
          : OUTLINE
        : o.danger
          ? QUIET_DANGER
          : QUIET;
  const ink = o.variant === 'on-accent' ? '' : 'text-base-muted disabled:text-base-disabled';
  return `inline-flex size-[var(--ds-target,1.75rem)] shrink-0 cursor-pointer items-center justify-center rounded-500 transition-colors disabled:cursor-not-allowed ${ink} ${state} ${SELECTED}`;
};

/**
 * An icon-only ActionButton: the 32px hit floor around a 16 or 18px glyph, a
 * gray (never accent) hover, `danger`, `disabled`, and the label doing double
 * duty as aria-label and tooltip. RowActions renders one per action; a module
 * mounts it directly wherever a glyph is the whole control.
 */
export function IconButton(props: IconButtonProps) {
  const size = () => props.size ?? 's';
  const tooltip = () => props.tooltip ?? true;
  const button = (cls?: string) => (
    <button
      type="button"
      id={props.id}
      class={`${iconButtonClasses({ variant: props.variant, danger: props.danger, tone: props.tone })} ${cls ?? ''}`}
      data-icon-button={size()}
      data-variant={props.variant ?? 'quiet'}
      data-danger={props.danger ? '' : undefined}
      disabled={props.disabled}
      aria-label={props.label}
      aria-pressed={props.pressed}
      aria-controls={props.controls}
      aria-expanded={props.expanded}
      aria-haspopup={props.haspopup}
      onMouseDown={props.onMouseDown}
      onClick={(e) => {
        if (!props.disabled) props.onClick?.(e);
      }}
    >
      <Dynamic component={props.icon} size={GLYPH[size()]} aria-hidden="true" />
    </button>
  );
  return (
    <Show when={tooltip()} fallback={button(props.class)}>
      {/* `inline-flex` over daisyUI's inline-block so the wrapper is exactly
          the button's box and sits level in a flex row; `shrink-0` because a
          32px target must never be squeezed by a truncating neighbour. */}
      <span
        class={`tooltip ${PLACEMENT[props.placement ?? 'top']} inline-flex shrink-0 ${props.class ?? ''}`}
        data-tip={props.disabled ? undefined : props.label}
      >
        {button()}
      </span>
    </Show>
  );
}
